import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../config/api";
import "./Supervisions.css";
import { formatDate } from "../utils/dateUtils";
import { HiRefresh } from "react-icons/hi";
import type { IconType } from "react-icons";
import {
  useSupervisionFilters,
  type Supervision,
  type SupervisionFilters,
} from "../hooks/useSupervisionFilters";
import Select from "react-select";
import { toast } from "react-toastify";
import { errorMessage } from "../utils/errors";
import { syncSummary } from "../utils/syncSummary";
import { syncEntity } from "../utils/syncEntity";
import { useChangesContext } from "../contexts/ChangesContext";
import { formatCodingType } from "../utils/codingUtils";
import { EMPTY_VALUE } from "../utils/display";
import { darkSelectStyles } from "../utils/selectStyles";
import {
  assignmentFilterOptions,
  ASSIGNMENT_FILTER_PLACEHOLDER,
  LATE_ASSIGNMENT_LABEL,
  lateAssignmentFromParams,
} from "../utils/assignmentFilter";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../utils/pagination";

/**
 * The status of a supervision, which is the only one it has: AIA stores `to_do` or
 * `done` in `supervisions.aia_supervision_status` and nothing else.
 *
 * The column used to show a status derived from the *game*'s metadata (Not Started /
 * In progress / Terminated / Canceled) under the same "Statut" heading, while this
 * one was labelled "Avancement" — two columns for what a reader takes to be one
 * thing, only one of which was about the supervision.
 */
const SUPERVISION_STATUS_LABELS: Record<string, string> = {
  to_do: "À faire",
  done: "Terminée",
};

const formatSupervisionStatus = (
  supervisionStatus: string | null | undefined,
): string =>
  (supervisionStatus && SUPERVISION_STATUS_LABELS[supervisionStatus]) ||
  EMPTY_VALUE;

/** Class modifier for the colour rules; `unknown` for the rows AIA left empty. */
const supervisionStatusSlug = (
  supervisionStatus: string | null | undefined,
): string =>
  supervisionStatus && SUPERVISION_STATUS_LABELS[supervisionStatus]
    ? supervisionStatus
    : "unknown";

// Typed explicitly to fix TS2786.
const RefreshIcon = HiRefresh as IconType;

/**
 * The invoice status cycle, in order.
 *
 * Single source for the three values: the cell shows what the next one will be, and a
 * second copy of the order would let the tooltip promise something else than the click
 * performs.
 */
const INVOICE_STATUS_CYCLE = ["non facturé", "facturé", "payé"] as const;

const INVOICE_STATUS_LABELS: Record<string, string> = {
  "non facturé": "Non Facturé",
  facturé: "Facturé",
  payé: "Payé",
};

/** Defaults to "non facturé", the state of a supervision never invoiced. */
const invoiceStatusOf = (status: string | null | undefined): string =>
  status && INVOICE_STATUS_LABELS[status] ? status : "non facturé";

const nextInvoiceStatus = (status: string | null | undefined): string => {
  const current = invoiceStatusOf(status);
  const index = INVOICE_STATUS_CYCLE.indexOf(current as any);
  return INVOICE_STATUS_CYCLE[(index + 1) % INVOICE_STATUS_CYCLE.length];
};

/** The class modifier: spaces become hyphens, so "non facturé" -> `non-facturé`. */
const invoiceStatusSlug = (status: string | null | undefined): string =>
  invoiceStatusOf(status).replace(/\s+/g, "-");

/** What the API applies: the filters and the sort. */
type SupervisionQuery = Pick<
  SupervisionFilters,
  | "dateFrom"
  | "dateTo"
  | "supervisor"
  | "status"
  | "lateAssignment"
  | "sortBy"
  | "sortOrder"
>;

const Supervisions: React.FC = () => {
  const [supervisions, setSupervisions] = useState<Supervision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [selectedSupervisions, setSelectedSupervisions] = useState<Set<number>>(
    new Set(),
  );

  // A sync can record supervisor changes it refuses to apply, so the Modifications
  // badge has to be recounted as soon as it finishes instead of on its next 30s poll.
  const { triggerRefresh } = useChangesContext();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Opened from the dashboard, the page carries its filter in the URL — the figure
  // clicked there and the rows shown here then describe the same set. Read once, as
  // the initial state: afterwards the dropdown owns the value.
  const [searchParams] = useSearchParams();
  const initialFilters = useMemo(
    () => ({ lateAssignment: lateAssignmentFromParams(searchParams) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Use the filtering hook
  const {
    filters,
    filteredSupervisions,
    updateFilter,
    updateSort,
    resetFilters,
    hasActiveFilters,
  } = useSupervisionFilters(supervisions, initialFilters);

  // Dropdown choices come from the API so they cover every supervision, not only
  // the ones on the current page.
  const [filterOptions, setFilterOptions] = useState<{
    supervisors: string[];
    statuses: string[];
  }>({ supervisors: [], statuses: [] });

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const response = await api.get("/api/supervisions/filter-options");
        setFilterOptions(response.data);
      } catch (error) {
        console.error("Error loading supervision filter options:", error);
      }
    };
    loadFilterOptions();
  }, []);

  // Memoised so the effect below re-runs on a real change, not on every render.
  const activeQuery = useMemo<SupervisionQuery>(
    () => ({
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      supervisor: filters.supervisor,
      status: filters.status,
      lateAssignment: filters.lateAssignment,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    }),
    [
      filters.dateFrom,
      filters.dateTo,
      filters.supervisor,
      filters.status,
      filters.lateAssignment,
      filters.sortBy,
      filters.sortOrder,
    ],
  );

  // Only the very first load shows the page-level spinner; later fetches reload
  // the rows in place, since the spinner replaces the whole page.
  const hasLoadedOnce = useRef(false);

  useEffect(() => {
    fetchSupervisions(
      currentPage,
      pageSize,
      activeQuery,
      hasLoadedOnce.current,
    );
    hasLoadedOnce.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, activeQuery]);

  /**
   * `silent` reloads the rows in place: no page-level spinner, and a failure is
   * reported with a toast instead of replacing the page with the error view.
   * Used after a sync, where the page is already on screen.
   */
  const fetchSupervisions = async (
    page: number = 1,
    limit: number = DEFAULT_PAGE_SIZE,
    query: SupervisionQuery,
    silent = false,
  ) => {
    try {
      if (!silent) setLoading(true);
      // Empty filters are omitted so the API sees only the active ones.
      const activeFilters = Object.fromEntries(
        Object.entries(query).filter(([, value]) => value),
      );
      const response = await api.get("/api/supervisions", {
        params: { page, limit, ...activeFilters },
      });
      setSupervisions(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
      setTotalItems(response.data.pagination.total);
      setCurrentPage(response.data.pagination.page);
    } catch (error) {
      if (silent) {
        toast.error("Erreur lors du rechargement des supervisions");
      } else {
        setError("Failed to load supervisions");
      }
      console.error("Error loading supervisions:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Any filter change re-queries the whole table, so the current page number is
  // meaningless against the new result set.
  const handleFilterChange = (key: keyof SupervisionFilters, value: string) => {
    updateFilter(key, value);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    resetFilters();
    setCurrentPage(1);
  };

  // A new sort reorders the whole table, so the current page number no longer points
  // at the same rows.
  const handleSortChange = (column: SupervisionFilters["sortBy"]) => {
    updateSort(column);
    setCurrentPage(1);
  };

  const syncSupervisions = async () => {
    try {
      setSyncing(true);

      const data = await syncEntity("supervisions");

      if (data.success) {
        // Refresh the rows in place, keeping the active filters
        await fetchSupervisions(currentPage, pageSize, activeQuery, true);
        triggerRefresh();
        toast.success(syncSummary(data));
      } else {
        toast.error("Échec de la synchronisation : " + (data.message ?? ""));
      }
    } catch (error) {
      toast.error("Échec de la synchronisation : " + errorMessage(error));
      console.error("Sync error:", error);
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleInvoiceStatus = async (supervision: Supervision) => {
    try {
      // Cycle: non facturé → facturé → payé → non facturé
      const newStatus = nextInvoiceStatus(supervision.invoice_status);

      await api.patch(`/api/supervisions/${supervision.id}/invoice-status`, {
        invoice_status: newStatus,
      });

      // Update local state
      setSupervisions((prev) =>
        prev.map((s) =>
          s.id === supervision.id ? { ...s, invoice_status: newStatus } : s,
        ),
      );
    } catch (error) {
      console.error("Error toggling invoice status:", error);
    }
  };

  /**
   * The supervisions a bulk invoice status can apply to.
   *
   * Selection serves only that, and a supervision with nobody assigned has nothing to
   * invoice — marking it "facturé" would also freeze the absence of a supervisor, so the
   * sync would refuse the eventual assignment and file it as a change to handle by hand.
   */
  const selectableSupervisions = useMemo(
    () => filteredSupervisions.filter((supervision) => supervision.supervisor),
    [filteredSupervisions],
  );

  const allSelectableSelected =
    selectableSupervisions.length > 0 &&
    selectableSupervisions.every((supervision) =>
      selectedSupervisions.has(supervision.id),
    );

  const handleSelectAllSupervisions = (checked: boolean) => {
    if (checked) {
      setSelectedSupervisions(
        new Set(selectableSupervisions.map((supervision) => supervision.id)),
      );
    } else {
      setSelectedSupervisions(new Set());
    }
  };

  const handleSelectSupervision = (supervisionId: number, checked: boolean) => {
    const newSelected = new Set(selectedSupervisions);
    if (checked) {
      newSelected.add(supervisionId);
    } else {
      newSelected.delete(supervisionId);
    }
    setSelectedSupervisions(newSelected);
  };

  const handleBulkUpdateInvoiceStatus = async (newStatus: string) => {
    try {
      const supervisionIds = Array.from(selectedSupervisions);

      await api.post("/api/supervisions/bulk-update-invoice-status", {
        supervisionIds: supervisionIds,
        invoice_status: newStatus,
      });

      // Update local state
      setSupervisions((prev) =>
        prev.map((supervision) =>
          supervisionIds.includes(supervision.id)
            ? { ...supervision, invoice_status: newStatus }
            : supervision,
        ),
      );

      setSelectedSupervisions(new Set());
    } catch (error) {
      console.error("Error bulk updating invoice status:", error);
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Chargement des supervisions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-error">
        <div className="alert alert-error">{error}</div>
        <button
          className="btn-primary"
          onClick={() => fetchSupervisions(currentPage, pageSize, activeQuery)}
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="supervisions-page">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <div>
              <h1 className="page-title">Supervisions</h1>
            </div>
            <div className="header-actions">
              <button
                className={`sync-button ${syncing ? "syncing" : ""}`}
                onClick={syncSupervisions}
                disabled={syncing}
              >
                {syncing ? "Synchronisation..." : "Synchroniser Supervisions"}
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions Bar for Invoice Status */}
        {selectedSupervisions.size > 0 && (
          <div className="bulk-actions-bar">
            <div className="bulk-actions-info">
              <input
                type="checkbox"
                checked={allSelectableSelected}
                onChange={(e) => handleSelectAllSupervisions(e.target.checked)}
                className="bulk-checkbox"
              />
              <span className="selection-count">
                {selectedSupervisions.size} supervision
                {selectedSupervisions.size > 1 ? "s" : ""} sélectionnée
                {selectedSupervisions.size > 1 ? "s" : ""}
              </span>
            </div>
            <div className="bulk-actions-buttons">
              <button
                className="bulk-action-btn btn-not-factured"
                onClick={() => handleBulkUpdateInvoiceStatus("non facturé")}
              >
                Marquer comme Non Facturé
              </button>
              <button
                className="bulk-action-btn btn-factured"
                onClick={() => handleBulkUpdateInvoiceStatus("facturé")}
              >
                Marquer comme Facturé
              </button>
              <button
                className="bulk-action-btn btn-paid"
                onClick={() => handleBulkUpdateInvoiceStatus("payé")}
              >
                Marquer comme Payé
              </button>
              <button
                className="bulk-action-btn btn-cancel"
                onClick={() => setSelectedSupervisions(new Set())}
              >
                ✕ Désélectionner
              </button>
            </div>
          </div>
        )}

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label>Date de début</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Date de fin</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Superviseur</label>
              <Select
                value={
                  filters.supervisor
                    ? { value: filters.supervisor, label: filters.supervisor }
                    : null
                }
                onChange={(option) =>
                  handleFilterChange("supervisor", option ? option.value : "")
                }
                options={[
                  { value: "", label: "Tous les superviseurs" },
                  ...filterOptions.supervisors.map((supervisor) => ({
                    value: supervisor,
                    label: supervisor,
                  })),
                ]}
                styles={darkSelectStyles}
                placeholder="Tous les superviseurs"
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <label>Statut</label>
              {/* The values sent to the API stay the stored ones (`to_do`, `done`);
                  only what is shown is translated, as in the table. */}
              <Select
                value={
                  filters.status
                    ? {
                        value: filters.status,
                        label: formatSupervisionStatus(filters.status),
                      }
                    : null
                }
                onChange={(option) =>
                  handleFilterChange("status", option ? option.value : "")
                }
                options={[
                  { value: "", label: "Tous les statuts" },
                  ...filterOptions.statuses.map((status) => ({
                    value: status,
                    label: formatSupervisionStatus(status),
                  })),
                ]}
                styles={darkSelectStyles}
                placeholder="Tous les statuts"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <label>Affectation</label>
              <Select
                value={
                  filters.lateAssignment
                    ? {
                        value: filters.lateAssignment,
                        label: LATE_ASSIGNMENT_LABEL,
                      }
                    : null
                }
                onChange={(option) =>
                  handleFilterChange(
                    "lateAssignment",
                    option ? option.value : "",
                  )
                }
                options={assignmentFilterOptions()}
                styles={darkSelectStyles}
                placeholder={ASSIGNMENT_FILTER_PLACEHOLDER}
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <button
                onClick={handleResetFilters}
                className="btn-secondary reset-filters"
                disabled={!hasActiveFilters}
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="supervisions-table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allSelectableSelected}
                      disabled={selectableSupervisions.length === 0}
                      onChange={(e) =>
                        handleSelectAllSupervisions(e.target.checked)
                      }
                      className="table-checkbox"
                    />
                  </th>
                  <th>ID</th>
                  <th>Match</th>
                  <th>Méthode de codage</th>
                  <th
                    className={`sortable ${
                      filters.sortBy === "date" ? filters.sortOrder : ""
                    }`}
                    onClick={() => handleSortChange("date")}
                  >
                    Date
                  </th>
                  <th
                    className={`sortable ${
                      filters.sortBy === "supervisor" ? filters.sortOrder : ""
                    }`}
                    onClick={() => handleSortChange("supervisor")}
                  >
                    Superviseur
                  </th>
                  {/*   <th>Contrat</th> */}
                  <th
                    className={`sortable ${
                      filters.sortBy === "supervisionStatus"
                        ? filters.sortOrder
                        : ""
                    }`}
                    onClick={() => handleSortChange("supervisionStatus")}
                  >
                    Statut
                  </th>
                  <th>Statut Facturation</th>
                </tr>
              </thead>
              <tbody>
                {filteredSupervisions.map((supervision) => (
                  <tr key={supervision.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedSupervisions.has(supervision.id)}
                        disabled={!supervision.supervisor}
                        title={
                          supervision.supervisor
                            ? undefined
                            : "Aucun superviseur assigné : rien à facturer"
                        }
                        onChange={(e) =>
                          handleSelectSupervision(
                            supervision.id,
                            e.target.checked,
                          )
                        }
                        className="table-checkbox"
                      />
                    </td>
                    <td>{supervision.aia_supervision_id}</td>
                    <td>{supervision.aia_game_name}</td>
                    <td>
                      {formatCodingType(
                        supervision.game_metadata?.coding_method,
                      )}
                    </td>
                    <td>{formatDate(supervision.aia_supervision_date)}</td>
                    <td>
                      {supervision.supervisor
                        ? `${supervision.supervisor.first_name} ${supervision.supervisor.last_name}`
                        : EMPTY_VALUE}
                    </td>
                    {/*     <td>{supervision.contract?.status || EMPTY_VALUE}</td> */}
                    <td>
                      <span
                        className={`status-badge ${supervisionStatusSlug(
                          supervision.aia_supervision_status,
                        )}`}
                      >
                        {formatSupervisionStatus(
                          supervision.aia_supervision_status,
                        )}
                      </span>
                    </td>
                    <td>
                      <div className="invoice-status-cell">
                        <span
                          className={`status-badge status-${invoiceStatusSlug(
                            supervision.invoice_status,
                          )}`}
                        >
                          {
                            INVOICE_STATUS_LABELS[
                              invoiceStatusOf(supervision.invoice_status)
                            ]
                          }
                        </span>
                        <button
                          type="button"
                          className="invoice-status-cycle"
                          onClick={() => handleToggleInvoiceStatus(supervision)}
                          // Nothing to invoice while nobody is on the supervision,
                          // and marking it "facturé" would freeze the supervisor
                          // that has not been assigned yet.
                          disabled={!supervision.supervisor}
                          // Names the next state rather than saying "click to
                          // change": the button is the only affordance left now
                          // that the text is inert, so it should say what it does.
                          title={
                            supervision.supervisor
                              ? `Passer à « ${
                                  INVOICE_STATUS_LABELS[
                                    nextInvoiceStatus(
                                      supervision.invoice_status,
                                    )
                                  ]
                                } »`
                              : "Aucun superviseur assigné : rien à facturer"
                          }
                          aria-label={
                            supervision.supervisor
                              ? `Passer à ${
                                  INVOICE_STATUS_LABELS[
                                    nextInvoiceStatus(
                                      supervision.invoice_status,
                                    )
                                  ]
                                }`
                              : "Aucun superviseur assigné : rien à facturer"
                          }
                        >
                          <RefreshIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="pagination-container">
            <div className="pagination-info">
              <span>
                Affichage de {(currentPage - 1) * pageSize + 1} à{" "}
                {Math.min(currentPage * pageSize, totalItems)} sur {totalItems}{" "}
                supervisions
              </span>
              <div className="page-size-selector">
                <label>Éléments par page</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="page-size-select"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="pagination-btn"
                title="Première page"
              >
                «
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
                title="Page précédente"
              >
                ‹
              </button>
              <div className="pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`pagination-btn ${
                        currentPage === pageNum ? "active" : ""
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
                title="Page suivante"
              >
                ›
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
                title="Dernière page"
              >
                »
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Supervisions;
